import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { colors } from '../../utils/colors';
import { Modal } from '../common/modal';
import { FormField, Select, TextArea } from '../common/form-elements';
import { Button } from '../common/button';
import { validators, validateForm } from '../../utils/validation';
import { useAriaAnnouncer } from '../../utils/accessibility';
import { Notification, useEmailService } from '../send-email/send-email';

const FloatingButton = styled(motion.button)`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  background: ${colors.primary};
  color: white;
  border: none;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px ${colors.shadow};
  z-index: 999;

  i {
    font-size: 1.5rem;
    transition: transform 0.3s ease;
  }

  &:hover {
    background: ${colors.primaryDark};
  }

  /* Attention-grabbing pulse animation */
  &.pulse-attention {
    animation: pulseGlow 2s ease-in-out;
    
    i {
      animation: iconShimmer 2s ease-in-out;
    }
    
    @keyframes pulseGlow {
      0% {
        box-shadow: 0 4px 12px ${colors.shadow};
        transform: scale(1);
      }
      25% {
        box-shadow: 0 4px 20px ${colors.primary}60, 0 0 20px ${colors.primary}40;
        transform: scale(1.03);
      }
      50% {
        box-shadow: 0 4px 25px ${colors.primary}80, 0 0 30px ${colors.primary}60;
        transform: scale(1.05);
      }
      75% {
        box-shadow: 0 4px 20px ${colors.primary}60, 0 0 20px ${colors.primary}40;
        transform: scale(1.03);
      }
      100% {
        box-shadow: 0 4px 12px ${colors.shadow};
        transform: scale(1);
      }
    }

    @keyframes iconShimmer {
      0%, 100% {
        transform: scale(1);
        color: white;
      }
      50% {
        transform: scale(1.1);
        color: #ffffff;
        text-shadow: 0 0 10px rgba(255, 255, 255, 0.8);
      }
    }
  }

  /* Gentle breathing animation for continuous subtle attention */
  &.breathing {
    animation: breathe 4s ease-in-out infinite;
    
    @keyframes breathe {
      0%, 100% {
        transform: scale(1);
        box-shadow: 0 4px 12px ${colors.shadow};
      }
      50% {
        transform: scale(1.02);
        box-shadow: 0 4px 15px ${colors.shadow}, 0 0 15px ${colors.primary}30;
      }
    }
  }
`;

const PulseWaves = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 60px;
  height: 60px;
  pointer-events: none;
  z-index: 998;

  &.active {
    .wave {
      animation: ripple 2s ease-out;
    }
    
    .wave:nth-child(2) {
      animation-delay: 0.3s;
    }
    
    .wave:nth-child(3) {
      animation-delay: 0.6s;
    }
  }

  .wave {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 60px;
    height: 60px;
    border: 2px solid ${colors.primary};
    border-radius: 50%;
    transform: translate(-50%, -50%);
    opacity: 0;
  }

  @keyframes ripple {
    0% {
      transform: translate(-50%, -50%) scale(1);
      opacity: 0.8;
      border-width: 2px;
    }
    50% {
      opacity: 0.4;
      border-width: 1px;
    }
    100% {
      transform: translate(-50%, -50%) scale(3);
      opacity: 0;
      border-width: 0px;
    }
  }
`;

const PopupMessage = styled(motion.div)`
  position: fixed;
  bottom: 6.5rem;
  right: 2rem;
  background: ${colors.white};
  color: ${colors.text};
  padding: 1rem 1.5rem;
  border-radius: 20px;
  box-shadow: 0 8px 24px ${colors.shadow};
  font-weight: 500;
  font-size: 0.9rem;
  z-index: 998;
  white-space: nowrap;
  border: 2px solid ${colors.primary};
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    right: 1.5rem;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid ${colors.white};
  }
  
  &::before {
    content: '';
    position: absolute;
    bottom: -10px;
    right: 1.5rem;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid ${colors.primary};
  }
`;

const BookingForm = styled(motion.form)`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${colors.primary};
    border-radius: 15px 15px 0 0;
  }
  
  @media (max-width: 767px) {
    gap: 1.5rem;
  }
`;

// Honeypot field styling - completely hidden from users but visible to bots
const HoneypotField = styled.input`
  position: absolute !important;
  left: -9999px !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0,0,0,0) !important;
  white-space: nowrap !important;
  border: 0 !important;
  opacity: 0 !important;
  pointer-events: none !important;
  tab-index: -1 !important;
`;

const FormSection = styled.div`
  display: grid;
  gap: 1.5rem;
  background: ${colors.white};
  
  @media (max-width: 767px) {
    gap: 1rem;
  }
`;

const FormRow = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (max-width: 767px) {
    gap: 0.75rem;
  }
`;

const FullWidthField = styled.div`
  grid-column: 1 / -1;
`;

const Description = styled.p`
  color: ${colors.textLight};
  margin-bottom: 2rem;
  font-size: 1.1rem;
  line-height: 1.6;
  border-left: 3px solid ${colors.primary};
  border-radius: 4px;
  padding-left: 1rem;
  margin: 1rem 0 2rem;
  
  @media (max-width: 767px) {
    font-size: 1rem;
    margin: 0.5rem 0 1.5rem;
    line-height: 1.5;
  }
`;

const serviceOptions = [
  { value: 'presencial', label: 'Presencial' },
  { value: 'online', label: 'Online' }
];

const timeOptions = [
  { value: 'morning', label: 'Mañana (9:00 - 12:00)' },
  { value: 'afternoon', label: 'Tarde (13:00 - 17:00)' },
  { value: 'evening', label: 'Noche (18:00 - 20:00)' }
];

const validationRules = {
  name: [validators.required, validators.humanName],
  email: [validators.required, validators.email, validators.noTempEmail],
  phone: [validators.required, validators.phone],
  consultationType: [validators.required],
  preferredTime: [validators.required],
  message: [
    validators.required, 
    validators.minLength(10), 
    validators.maxLength(1000),
    validators.noSpamKeywords,
    validators.noRepeatedChars,
    validators.maxLinks(1)
  ]
};

export const AgendaButton = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [shouldPulse, setShouldPulse] = useState(false);
  const [showWaves, setShowWaves] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    consultationType: '',
    preferredTime: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const formRef = useRef(null);
  const announce = useAriaAnnouncer();
  
  // Use the email service
  const { submitConsultationForm, loading, notification, botProtection } = useEmailService('agenda');

  React.useEffect(() => {
    const handleOpenAgendaModal = () => setIsModalOpen(true);
    window.addEventListener('openAgendaModal', handleOpenAgendaModal);
    return () => window.removeEventListener('openAgendaModal', handleOpenAgendaModal);
  }, []);

  // Pulse animation every 10 seconds (5 seconds for testing)
  React.useEffect(() => {
    const pulseInterval = setInterval(() => {
      if (!isModalOpen) {
        setShouldPulse(true);
        setShowWaves(true);
        setTimeout(() => {
          setShouldPulse(false);
          setShowWaves(false);
        }, 2000); // Duration of pulse animation
      }
    }, 5000); // Changed to 5 seconds for easier testing

    return () => clearInterval(pulseInterval);
  }, [isModalOpen]);

  // Popup message every 30 seconds
  React.useEffect(() => {
    const popupInterval = setInterval(() => {
      if (!isModalOpen) {
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 5000); // Hide after 5 seconds
      }
    }, 15000);

    return () => clearInterval(popupInterval);
  }, [isModalOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Record user activity for bot protection
    botProtection.recordActivity('typing');
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { isValid, errors: validationErrors } = validateForm(formData, validationRules);

    if (!isValid) {
      setErrors(validationErrors);
      announce('Por favor, corrija los errores en el formulario.', 'assertive');
      return;
    }

    try {
      // Use the email service to send consultation form
      const result = await submitConsultationForm(formData);
      
      if (result.success) {
        // Close modal immediately
        setIsModalOpen(false);
        
        announce('Solicitud de cita enviada exitosamente.', 'polite');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          consultationType: '',
          preferredTime: '',
          message: ''
        });
      } else if (result.blocked) {
        if (result.isRateLimit) {
          // Clear form for rate limit messages since they indicate request was received
          setFormData({
            name: '',
            email: '',
            phone: '',
            consultationType: '',
            preferredTime: '',
            message: ''
          });
          announce('Tu solicitud ya fue recibida. Nos pondremos en contacto pronto.', 'polite');
          setIsModalOpen(false); // Close modal for acknowledgment messages too
        } else {
          announce('Envío bloqueado por seguridad.', 'assertive');
        }
      } else {
        announce('Error al enviar la solicitud. Por favor, intente nuevamente.', 'assertive');
      }
      
    } catch (error) {
      announce('Error al enviar la solicitud. Por favor, intente nuevamente.', 'assertive');
    }
  };

  return (
    <>
      <PulseWaves className={showWaves ? 'active' : ''}>
        <div className="wave"></div>
        <div className="wave"></div>
        <div className="wave"></div>
      </PulseWaves>
      
      <FloatingButton
        onClick={() => setIsModalOpen(true)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        className={`${shouldPulse ? 'pulse-attention' : ''} breathing`}
        aria-label="Agendar cita"
      >
        <i className="fa-regular fa-calendar" aria-hidden="true"></i>
      </FloatingButton>

      <AnimatePresence>
        {showPopup && (
          <PopupMessage
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 10 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            onClick={() => setIsModalOpen(true)}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            aria-label="Hacer clic para agendar consulta"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsModalOpen(true);
              }
            }}
          >
            <i className="fa-regular fa-calendar" aria-hidden="true"></i> Agenda tu consulta ahora!
          </PopupMessage>
        )}
      </AnimatePresence>

      <Modal
        isOpen={isModalOpen}
        onClose={() => !loading && setIsModalOpen(false)}
        title="Agenda tu Consulta"
      >
        <Description>
          Completa el formulario y me pondré en contacto contigo para confirmar tu cita.
        </Description>
        
        <BookingForm 
          ref={formRef}
          onSubmit={handleSubmit}
          noValidate
          aria-label="Formulario de agenda"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          onMouseMove={() => botProtection.recordActivity('mouse')}
        >
          {/* Honeypot field - invisible to users, visible to bots */}
          <HoneypotField
            name={botProtection.honeypotField}
            value={formData[botProtection.honeypotField] || ''}
            onChange={handleChange}
            tabIndex="-1"
            autoComplete="off"
            aria-hidden="true"
          />
          
          <FormSection>
            {/* Row 1: Name and Email */}
            <FormRow>
              <FormField
                label="Nombre completo"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={errors.name}
                disabled={loading}
                required
                placeholder="Tu nombre completo"
                aria-required="true"
                aria-invalid={errors.name ? 'true' : 'false'}
                aria-describedby={errors.name ? 'name-error' : undefined}
                onFocus={() => botProtection.recordActivity('mouse')}
                onKeyDown={() => botProtection.recordActivity('keyboard')}
              />

              <FormField
                label="Email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                disabled={loading}
                required
                placeholder="tu@email.com"
                aria-required="true"
                aria-invalid={errors.email ? 'true' : 'false'}
                aria-describedby={errors.email ? 'email-error' : undefined}
                onFocus={() => botProtection.recordActivity('mouse')}
                onKeyDown={() => botProtection.recordActivity('keyboard')}
              />
            </FormRow>

            {/* Row 2: Phone and Consultation Type */}
            <FormRow>
              <FormField
                label="Teléfono"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                error={errors.phone}
                disabled={loading}
                required
                placeholder="+598 99 123 456"
                aria-required="true"
                aria-invalid={errors.phone ? 'true' : 'false'}
                aria-describedby={errors.phone ? 'phone-error' : undefined}
              />

              <Select
                label="Tipo de consulta"
                name="consultationType"
                value={formData.consultationType}
                onChange={handleChange}
                options={serviceOptions}
                error={errors.consultationType}
                disabled={loading}
                required
                aria-required="true"
                aria-invalid={errors.consultationType ? 'true' : 'false'}
                aria-describedby={errors.consultationType ? 'consultationType-error' : undefined}
              />
            </FormRow>

            {/* Row 3: Preferred Time (full width on mobile, half width on desktop) */}
            <FormRow>
              <Select
                label="Horario preferido"
                name="preferredTime"
                value={formData.preferredTime}
                onChange={handleChange}
                options={timeOptions}
                error={errors.preferredTime}
                disabled={loading}
                required
                aria-required="true"
                aria-invalid={errors.preferredTime ? 'true' : 'false'}
                aria-describedby={errors.preferredTime ? 'preferredTime-error' : undefined}
              />
            </FormRow>

            {/* Row 4: Reason for consultation (full width) */}
            <FullWidthField>
              <TextArea
                label="Motivo de consulta"
                name="message"
                value={formData.message}
                onChange={handleChange}
                error={errors.message}
                disabled={loading}
                required
                placeholder="Describe el motivo de tu consulta..."
                rows={3}
                aria-required="true"
                aria-invalid={errors.message ? 'true' : 'false'}
                aria-describedby={errors.message ? 'message-error' : undefined}
              />
            </FullWidthField>
          </FormSection>

          <Button 
            type="submit"
            disabled={loading}
            fullWidth
            icon={loading ? 'fa-solid fa-spinner fa-spin' : 'fa-regular fa-calendar-check'}
            aria-label={loading ? 'Enviando solicitud...' : 'Enviar solicitud de cita'}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {loading ? 'Enviando...' : 'Solicitar Cita'}
          </Button>
        </BookingForm>
      </Modal>

      {/* Notification from email service */}
      <AnimatePresence>
        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => {}} // The email service handles auto-hide
          />
        )}
      </AnimatePresence>
    </>
  );
};
